// Hooks de sincronização — extraído de App.jsx — Fase C
import { DB } from "../lib/deviceStorage";
import { useEffect, useContext, useCallback } from "react";
import { useSync } from "../sync/SyncProvider";
import { GLOBAL_COLLECTIONS, GLOBAL_KEY } from "../config/appConfig";
import { pushGlobal } from "../sync/collectionApi";

// FarmCtx importado de farmHooks para manter dependência unidirecional
import { FarmCtx } from "./farmHooks";

// Permite que módulos externos sinalizem que há edição em andamento
let _isEditing = false; // eslint-disable-line no-unused-vars
export function setEditingFlag(v) { _isEditing = v; }

function useBgSyncToast(collection, show) {
  const { fid } = useContext(FarmCtx) || {};
  useEffect(() => {
    if (!collection || !show) return;
    function handler(e) {
      const keys = e.detail?.keys || [];
      const myKey = fid ? `fz:${fid}:${collection}` : null;
      if (myKey && keys.includes(myKey)) {
        show("🔄 Dados atualizados por outro usuário", "info");
      }
    }
    window.addEventListener("fz:bg:updated", handler);
    return () => window.removeEventListener("fz:bg:updated", handler);
  }, [collection, fid, show]);
}

// Hook: call after save/edit to trigger targeted sync for that collection
// Usage: const syncOnSave = useSyncOnSave();  syncOnSave("pastures")
function useSyncOnSave() {
  const ctx  = useSync();
  const farm = useContext(FarmCtx);
  const fid  = farm?.fid;
  // Accepts (collection) or (farmId, collection) for cross-farm syncs
  return useCallback((collectionOrFarmId, maybeCollection) => {
    const [targetFid, col] = maybeCollection
      ? [collectionOrFarmId, maybeCollection]   // called as (farmId, collection)
      : [fid, collectionOrFarmId];              // called as (collection)
    if (!ctx || !col) return;
    // Global collections use pushGlobal, not syncAfterSave
    if (GLOBAL_COLLECTIONS.includes(col)) {
      if (navigator.onLine) {
        setTimeout(async () => {
          try {
            const key = GLOBAL_KEY(col);
            const locData = JSON.parse(localStorage.getItem(key)||"[]");
            const res = await pushGlobal(key, locData, "sync-on-save");
            if (res?.version) await DB.set(`${key}:_ver`, res.version);
          } catch(e) {}
        }, 400);
      }
      return;
    }
    if (!targetFid) return;
    // Pequeno atraso para garantir que o IndexedDB recebeu o write local
    // antes do sync calcular o oplog. Evita corrida em que o pull seguinte
    // ainda enxerga o estado antigo e reverte a tela.
    setTimeout(() => {
      try { ctx.syncAfterSave(targetFid, col); } catch(e) {}
    }, 250);
  }, [ctx, fid]);
}

// Stronger targeted sync for modules whose records were surviving only locally.
// It keeps the regular save-triggered sync and schedules two follow-up attempts
// to confirm the collection is pushed even if bootstrap/rehydration races happen.
function useReliableSyncOnSave() {
  const ctx  = useSync();
  const farm = useContext(FarmCtx);
  const fid  = farm?.fid;
  const baseSyncOnSave = useSyncOnSave();
  return useCallback((collectionOrFarmId, maybeCollection) => {
    const [targetFid, col] = maybeCollection
      ? [collectionOrFarmId, maybeCollection]
      : [fid, collectionOrFarmId];
    if (!ctx || !targetFid || !col || GLOBAL_COLLECTIONS.includes(col)) {
      baseSyncOnSave(collectionOrFarmId, maybeCollection);
      return;
    }

    // v2.220.06/v3.6.01: módulos operacionais entram no push urgente imediatamente
    // também nos fluxos de atualização/análise/aprovação. O useSyncOnSave base
    // espera 250ms para dar tempo do IndexedDB gravar; isso é seguro para a
    // maioria dos módulos, mas em orders/services/occurrences o useFarmCollection
    // já atualiza o live cache de forma síncrona. Chamar syncAfterSave agora faz
    // a fila urgente ser persistida antes que o usuário saia da tela/app e evita
    // que a alteração fique só local até o próximo scheduler operacional.
    if (["orders", "services", "occurrences"].includes(String(col))) {
      // v3.4.02: T0 — save_called (registra junto com a chamada do hook).
      try {
        if (typeof window !== "undefined") {
          window.__fzSaveCalledAt = Date.now();
          window.__fzSaveCalledFarmCollection = `${targetFid}:${col}`;
        }
      } catch (_) {}
      // v2.221.26/v3.6.01: módulos operacionais usam uma única tentativa curta pós-save.
      // PRV pendente permanece preservado até ACK, e os ciclos normais/urgentes com
      // lock/backoff cuidam do retry sem criar rajada de chamadas que pode gerar 429.
      setTimeout(() => {
        try { ctx.syncAfterSave(targetFid, col); } catch(e) {}
      }, 450);
      // v3.9.16: retry tardio para lane operacional.
      // Cobre gate/lock/cache ainda não estabilizado na primeira tentativa.
      setTimeout(() => {
        try { ctx.syncAfterSave(targetFid, col); } catch(e) {}
      }, 3500);
      return;
    }

    baseSyncOnSave(collectionOrFarmId, maybeCollection);
    // v2.241.01: retries pós-save ficam mantidos em 2 tentativas e são espaçados
    // para reduzir rajadas no HostGator sem prejudicar o ACK dos módulos operacionais.
    [600, 2200].forEach((delay) => {
      setTimeout(() => {
        try { ctx.syncAfterSave(targetFid, col); } catch(e) {}
      }, delay);
    });
  }, [baseSyncOnSave, ctx, fid]);
}


export { useBgSyncToast, useSyncOnSave, useReliableSyncOnSave };
